import React, { useMemo, useState } from 'react';
import { compact } from 'lodash';
import { useHistory } from 'react-router-dom';
import { Button, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import { routes } from "../../../constants";
import { selectors } from "../../../store/state-selectors";
import { useOnMount } from "../../../hooks/useOnMount";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import TableFilters from "../components/TableFilters/TableFilters";
import FutureInvoiceRow from "./FutureInvoiceRow";
import { FilterType, filterLabels } from "./types";
import { makeFilterConfig, futureInvoicesReportColumnDefs, headerItems, makeCsv, getDefaultSort } from "./FutureInvoices.utils";
import styles from "./FutureInvoices.module.scss";
const testIdPrefix = 'future-invoices-report';
const title = 'Future Invoices Report';
export const FutureInvoicesReport = () => {
  const history = useHistory();
  const [futureInvoicesReportData, setFutureInvoicesReportData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const {
    downloadCsv,
    getNow
  } = useIoContext();
  const sortIds = compact(headerItems.map(item => item.sortId));
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const handleFetchFilterConfig = async () => {
    const [companyNames, agreementNames] = await Promise.all([api.reports.getFutureInvoicesCompanyNames(), api.reports.getFutureInvoicesAgreementNames()]);
    const filterOptions = {
      [FilterType.CompanyName]: companyNames,
      [FilterType.AgreementName]: agreementNames
    };
    return makeFilterConfig(filterOptions, getNow());
  };
  const fetchFutureInvoicesReport = (selectedFilters, sort) => {
    var _selectedFilters$Filt, _selectedFilters$Filt2, _selectedFilters$Filt3, _selectedFilters$Filt4;
    if (businessId == null) return;
    setIsLoading(true);
    api.reports.getFutureInvoicesReport({
      companies: selectedFilters[FilterType.CompanyName],
      agreements: selectedFilters[FilterType.AgreementName],
      requiresApproval: selectedFilters[FilterType.RequiresApproval],
      issueDateFrom: (_selectedFilters$Filt = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.from,
      issueDateTo: (_selectedFilters$Filt2 = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.to,
      dueDateFrom: (_selectedFilters$Filt3 = selectedFilters[FilterType.DueDate]) === null || _selectedFilters$Filt3 === void 0 ? void 0 : _selectedFilters$Filt3.from,
      dueDateTo: (_selectedFilters$Filt4 = selectedFilters[FilterType.DueDate]) === null || _selectedFilters$Filt4 === void 0 ? void 0 : _selectedFilters$Filt4.to,
      sortField: sort ? sortIds[sort.colIndex] : undefined,
      sortDirection: sort === null || sort === void 0 ? void 0 : sort.sort
    }).then(setFutureInvoicesReportData).finally(() => setIsLoading(false));
  };
  useOnMount(() => {
    document.title = title;
    fetchFutureInvoicesReport(selectedFilters, sort);
  });
  const onExport = () => {
    downloadCsv(makeCsv(futureInvoicesReportData), 'future-invoices.csv');
  };
  const onSortChange = sort => {
    changeSort(sort);
    fetchFutureInvoicesReport(selectedFilters, sort);
  };
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": testIdPrefix,
    header: title,
    variant: "containedHeight",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/React.createElement(Button, {
      "data-testid": "".concat(testIdPrefix, "-export-btn"),
      disabled: isLoading,
      onClick: onExport
    }, "Export")
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: futureInvoicesReportColumnDefs,
    numRows: futureInvoicesReportData.length,
    sort: sort,
    onSortChange: onSortChange
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.pageContent
  }, /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: handleFetchFilterConfig,
    renderLoader: () => /*#__PURE__*/React.createElement(Spinner, {
      size: 24
    }),
    renderChild: _ref => {
      let {
        data: filterConfig
      } = _ref;
      return /*#__PURE__*/React.createElement(TableFilters, {
        testIdPrefix: testIdPrefix,
        filterLabels: filterLabels,
        filterConfig: filterConfig,
        selectedFilters: selectedFilters,
        setSelectedFilters: setSelectedFilters,
        onRefresh: fetchFutureInvoicesReport
      });
    }
  }))), /*#__PURE__*/React.createElement(TableList.Structure, {
    header: /*#__PURE__*/React.createElement(TableList.Header, {
      stickyToTop: true,
      items: headerItems,
      "data-testid": "".concat(testIdPrefix, "-table-header")
    }),
    content: isLoading ? /*#__PURE__*/React.createElement(Spinner, {
      centeredInBox: true
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, futureInvoicesReportData.map((futureInvoice, index) => /*#__PURE__*/React.createElement(FutureInvoiceRow, {
      key: futureInvoice.compoundId(),
      futureInvoice: futureInvoice,
      testId: "".concat(testIdPrefix, "-table-row-").concat(index)
    })))
  })));
};